'use client';

import { COMPANY_COUNTRIES } from '@/lib/countries';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { BalanceSheetFlatRow } from '@/new_components/balance_sheet/balanceSheetService';
import type { GuvFlatRow } from '@/new_components/guv/guvHistoryService';
import { useTranslation } from 'react-i18next';
import { trackCompanyView } from '@/lib/analytics';
import type {
  CompanyBasicResponse,
  CompanyContactResponse,
  CompanyFinancialsResponse,
  CompanyGesellschafterResponse,
  CompanyHistory,
  CompanyLegalStatus,
  CompanyRegisterResponse,
} from '@/lib/backend-api';
import { companyCountryCode } from '@/lib/company-utils';
import { FreezeReporter } from '@/lib/freeze-reporter';
import { isAdmin, isPro } from '@/lib/permissions';
import { useRecordCompanyView } from '@/hooks/use-recent-activity';
import { useSession } from '@/hooks/use-session';
import { Container } from '@/components/common/container';
import {
  backfillAiSummaryAction,
  triggerAiSummaryForGuestIfFirstVisit,
} from '@/lib/actions/backfill';
import {
  buildCompanyProfileHeroData,
  buildCompanyProfileStatistics,
  getEffectiveCompanyRegisterData,
  getEffectiveCompanyFinancialsData,
  formatRegisterNumber,
} from './companyDetailUtils';
import { createFormatters } from './currencyFormatters';
import { MobileActionBar } from './MobileActionBar';
import { RegisterSourceNote } from './RegisterSourceNote';
import {
  CompanyDetailLoading,
  type CompanyDetailPageProps,
  FINANCIAL_DOCS_ANCHOR,
  REGISTER_DOCS_ANCHOR,
} from './companyDetailLayout';
import {
  buildAiGuvRows,
  buildAktivaRows,
  buildCompanyViewInput,
  buildGuvRows,
  buildPassivaRows,
  buildRevenueHistory,
  buildValidationErrors,
  collectAvailableFinancialYears,
  computeHasCostBreakdown,
  hasDownloadableFinancialDocs,
  resolveLegalStatus,
} from './companyDetailDerived';
import { CompanyDetailMainColumn } from './CompanyDetailMainColumn';
import { CompanyDetailSidebarColumn } from './CompanyDetailSidebarColumn';

/**
 * CompanyDetailPage — main company detail view using unified backend endpoints.
 *
 * This component makes only 5 API calls instead of 15+:
 * 1. /unified/basic - Fast initial load (SSR for guests)
 * 2. /unified/contact - Website & social (auto-backfills)
 * 3. /unified/register - Register + location + NACE (auto-backfills, SI+CD scraping)
 * 4. /unified/gesellschafter - Shareholders + DK file info (DK scraping)
 * 5. /unified/financials - Financial data + relationships + AI enrichments (auto-backfills)
 *
 * Guest users skip calls 2-5 and render from basicData only (no backfills triggered).
 * All backfill logic is handled by the backend - no conditional backfill logic needed here.
 *
 * ## CLS (Cumulative Layout Shift) Prevention Rules
 *
 * The SSR page must produce a stable layout for guests. Logged-in users get
 * progressive updates via client-side fetches, but the initial frame must not
 * jump. Follow these rules when adding or modifying sections:
 *
 * 1. **Parent conditions must match child thresholds.**
 *    If a chart component returns null when `data.length < N`, the parent
 *    conditional must use the SAME threshold. Otherwise a loading skeleton
 *    renders for the chunk, then the real component returns null → shift.
 *    Example: a chart that needs >= 50 values, so the parent checks >= 50.
 *
 * 2. **Gated sections must not flash a skeleton while permissions resolve**
 *    (session + guest-permission fetch): hidden gates render null during
 *    loading, visible ones render their children.
 *
 * 3. **Dynamic imports (`next/dynamic`) use `ssr: false` for recharts.**
 *    Recharts requires DOM measurement and can't SSR. The loading skeleton
 *    MUST match the expected rendered height. If the chart may return null
 *    based on data, tighten the parent condition (rule 1) so the skeleton
 *    never appears for cases that would produce null.
 *
 * 4. **Map containers need fixed height + background.**
 *    Leaflet is client-only. The `h-[200px] bg-muted/30` ensures the
 *    container occupies stable space before tiles load.
 *
 * 5. **Never conditionally render a section based on async-only data.**
 *    If data comes from a client fetch (contactData, financialsData), the
 *    section must either always render (with an empty/fallback state) or
 *    derive its condition from SSR-available basicData via effectiveXData.
 */
/**
 * Dev-only diagnostic logging.
 *
 * The Gesellschafterliste poll below fires up to 24 times per page view, so
 * in production it filled the console with two dozen lines per visit —
 * drowning anything a user or a support engineer actually needed to see.
 * Stripped entirely from production builds by the NODE_ENV check.
 */
const debugLog = (...args: unknown[]) => {
  if (process.env.NODE_ENV === 'development') console.info(...args);
};

export function CompanyDetailPage({
  euId,
  basicData: ssrBasicData,
  networkData: ssrNetworkData,
  topEmployerRank,
}: CompanyDetailPageProps) {
  const { data: session, status: sessionStatus } = useSession();
  const { t, i18n } = useTranslation();
  const recordCompanyView = useRecordCompanyView();
  const recordedCompanyViewsRef = useRef(new Set<string>());
  const fmt = useMemo(() => createFormatters(i18n.language), [i18n.language]);

  // ==========================================================================
  // STATE - one state per unified endpoint
  // ==========================================================================
  const [basicData, setBasicData] = useState<CompanyBasicResponse | null>(
    ssrBasicData ?? null,
  );
  const [contactData, setContactData] = useState<CompanyContactResponse | null>(
    null,
  );
  const [registerData, setRegisterData] =
    useState<CompanyRegisterResponse | null>(null);
  const [gesellschafterData, setGesellschafterData] =
    useState<CompanyGesellschafterResponse | null>(null);
  const [companyHistory, setCompanyHistory] = useState<CompanyHistory | null>(
    ssrBasicData?.company_history ?? null,
  );
  const [financialsData, setFinancialsData] =
    useState<CompanyFinancialsResponse | null>(null);
  const insolvencySummary =
    basicData?.insolvency ?? registerData?.insolvency ?? null;
  const insolvencyRecords = insolvencySummary?.records ?? [];
  // Resolved from the merged SI + CD + portal timeline. `company_status` is
  // the legacy binary flag and only ever says active/insolvent, so it could
  // not express a voluntary liquidation; `dissolved` alone could not tell a
  // company still being wound up from one already wound down.
  const legalStatus: CompanyLegalStatus = resolveLegalStatus(insolvencySummary);
  const [loading, setLoading] = useState(!ssrBasicData);
  const [loadingBackground, setLoadingBackground] = useState(false);
  const [loadingRegister, setLoadingRegister] = useState(false);
  const [loadingGesellschafter, setLoadingGesellschafter] = useState(false);
  const [_error, setError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState<Set<string>>(
    new Set(),
  );

  const isAdminUser = isAdmin(session);
  // Pro or Admin — gates the Bilanz PDF download in FinancialDocsCard.
  const isProOrAdmin = isAdminUser || isPro(session);

  // Wrapper setters that also update companyHistory with the latest value
  const handleSetBasicData = useCallback(
    (data: CompanyBasicResponse | null) => {
      setBasicData(data);
      if (data?.company_history) setCompanyHistory(data.company_history);
    },
    [],
  );
  const handleSetRegisterData = useCallback(
    (data: CompanyRegisterResponse | null) => {
      setRegisterData(data);
      if (data?.company_history) setCompanyHistory(data.company_history);
    },
    [],
  );

  // ==========================================================================
  // DATA FETCHING - 4 parallel calls, no backfill logic needed
  // ==========================================================================

  // Ref to prevent duplicate initial fetches in StrictMode
  const hasFetchedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async () => {
    // Abort any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setLoading(true);
    setError(null);

    const deniedSet = new Set<string>();

    // Helper to process a single response and update state immediately
    const processResponse = async <T,>(
      response: Response,
      key: string,
      setter: (data: T) => void,
    ): Promise<boolean> => {
      if (signal.aborted) return false;
      if (response.status === 403) {
        deniedSet.add(key);
        if (!signal.aborted) setPermissionDenied(new Set(deniedSet));
        return false;
      } else if (response.ok) {
        const json = await response.json();
        if (!signal.aborted) {
          setter(json.data);
        }
        return true;
      } else if (response.status === 429) {
        return false;
      }
      return false;
    };

    try {
      // Fire contact + register endpoints immediately (they run in parallel)
      // Each promise's .then() calls processResponse which updates state
      // immediately when data arrives — the UI re-renders progressively.
      const contactPromise = fetch(`/api/companies/${euId}/unified/contact`, {
        signal,
      })
        .then((res) => processResponse(res, 'contact', setContactData))
        .catch(() => false);

      setLoadingRegister(true);
      const registerPromise = fetch(`/api/companies/${euId}/unified/register`, {
        signal,
      })
        .then((res) => processResponse(res, 'register', handleSetRegisterData))
        .catch(() => false)
        .finally(() => {
          if (!signal.aborted) setLoadingRegister(false);
        });

      setLoadingGesellschafter(true);
      const gesellschafterPromise = fetch(
        `/api/companies/${euId}/unified/gesellschafter`,
        { signal },
      )
        .then((res) =>
          processResponse(res, 'gesellschafter', setGesellschafterData),
        )
        .catch(() => false)
        .finally(() => {
          if (!signal.aborted) setLoadingGesellschafter(false);
        });

      // Use SSR basic data if available, otherwise fetch from API
      let parsedBasicData: CompanyBasicResponse | null = ssrBasicData ?? null;
      if (!ssrBasicData) {
        const basicRes = await fetch(`/api/companies/${euId}/unified/basic`, {
          signal,
        });
        if (signal.aborted) return;

        if (basicRes.ok) {
          const json = await basicRes.json();
          parsedBasicData = json.data;
          if (!signal.aborted) {
            handleSetBasicData(parsedBasicData);
          }
        } else if (basicRes.status === 403) {
          deniedSet.add('basic');
        }
      }

      // Skip financials + AI enrichments for VR (Vereinsregister) companies
      const isVR = parsedBasicData?.register_type === 'VR';
      // The financials response is the only source of `has_file` on
      // financial_files — without it the Jahresabschluss buttons stay
      // disabled (basicData deliberately omits storage info). So it must
      // land fast: phase 1 is a cached read (refresh=false) that serves
      // already-stored files immediately. Phase 2 re-fetches with the
      // backend's on-demand Bundesanzeiger refresh (its default), which
      // downloads only publications not yet on file — worst case minutes —
      // and runs detached when phase 1 succeeded, silently swapping in any
      // newly found files. A transient 429/5xx/timeout on the refresh must
      // not strand the page on stale data until a manual reload, so it
      // retries with backoff; 403/404 are definitive and go straight to
      // processResponse.
      const refreshFinancialsWithRetry = async (): Promise<boolean> => {
        const retryDelaysMs = [2000, 5000];
        for (let attempt = 0; ; attempt++) {
          const isLastAttempt = attempt >= retryDelaysMs.length;
          try {
            const res = await fetch(
              `/api/companies/${euId}/unified/financials`,
              { signal },
            );
            if (
              res.ok ||
              res.status === 403 ||
              res.status === 404 ||
              isLastAttempt
            ) {
              return processResponse(res, 'financials', setFinancialsData);
            }
          } catch {
            if (signal.aborted || isLastAttempt) return false;
          }
          await new Promise((resolve) =>
            setTimeout(resolve, retryDelaysMs[attempt]),
          );
          if (signal.aborted) return false;
        }
      };
      const fetchFinancials = async (): Promise<boolean> => {
        try {
          const cachedRes = await fetch(
            `/api/companies/${euId}/unified/financials?refresh=false`,
            { signal },
          );
          const gotCached = await processResponse(
            cachedRes,
            'financials',
            setFinancialsData,
          );
          // 403/404 are definitive — no point firing the refresh pass.
          if (cachedRes.status === 403 || cachedRes.status === 404) {
            return gotCached;
          }
          if (gotCached) {
            // Detached on purpose: the page must not hold its background-
            // loading state hostage to a scrape that can take minutes.
            void refreshFinancialsWithRetry().catch(() => {});
            return true;
          }
        } catch {
          if (signal.aborted) return false;
        }
        // Cached read failed — fall back to the blocking refresh path.
        return refreshFinancialsWithRetry();
      };
      const financialsPromise = isVR
        ? Promise.resolve(false)
        : fetchFinancials();

      // Page can render with basic data — stop showing loading state immediately
      if (!signal.aborted) {
        setLoading(false);
        setPermissionDenied(new Set(deniedSet));
        setLoadingBackground(true);
      }

      // Wait for all fetches — each one already updates state via processResponse
      // as soon as it resolves, so the UI updates progressively
      await Promise.allSettled([
        contactPromise,
        registerPromise,
        gesellschafterPromise,
        financialsPromise,
      ]);

      if (!signal.aborted) {
        setLoadingBackground(false);
      }
    } catch {
      if (!signal.aborted) {
        setError('Failed to load company data');
        setLoading(false);
      }
    }
  }, [euId, ssrBasicData, handleSetBasicData, handleSetRegisterData]);

  // Initial fetch - only once per euId change
  useEffect(() => {
    hasFetchedRef.current = false;
  }, [euId]);

  useEffect(() => {
    // Wait until session status is resolved
    if (sessionStatus === 'loading') return;

    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      if (session) {
        // Authenticated: fetch all endpoints (progressive loading with backfills)
        fetchData();
      } else {
        // Guest: skip all endpoint calls, basicData from SSR is sufficient
        setLoading(false);
      }
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData, session, sessionStatus]);

  // Fire AI summary backfill once per euId, after financials lands and only
  // when the company has a balance sheet but no summary yet. The action is
  // idempotent (backend short-circuits "already_parsed"), and we refetch the
  // financials endpoint when it resolves so the UI fills in without a refresh.
  const aiSummaryFiredRef = useRef<string | null>(null);
  useEffect(() => {
    if (!session) return;
    if (!financialsData) return;
    if (aiSummaryFiredRef.current === euId) return;
    if (financialsData.ai_summary || financialsData.ai_summary_en) return;

    const hasBalanceSheet =
      (financialsData.aktiva_data?.length ?? 0) > 0 ||
      (financialsData.passiva_data?.length ?? 0) > 0;
    if (!hasBalanceSheet) return;

    aiSummaryFiredRef.current = euId;

    const controller = new AbortController();
    void backfillAiSummaryAction(euId)
      .then(async (res) => {
        if (controller.signal.aborted || !res.success) return;
        // Cached read: this refetch only wants the freshly parsed AI
        // summary fields — refresh=true would queue behind an in-flight
        // publications scrape for the same company.
        const refetch = await fetch(
          `/api/companies/${euId}/unified/financials?refresh=false`,
          { signal: controller.signal },
        );
        if (controller.signal.aborted || !refetch.ok) return;
        const json = await refetch.json();
        if (!controller.signal.aborted) setFinancialsData(json.data);
      })
      .catch(() => {});

    return () => controller.abort();
  }, [euId, session, financialsData]);

  // The /unified/gesellschafter call above may TRIGGER a backend parse of the
  // Gesellschafterliste (gesellschafter_backfill_triggered) whose result lands
  // a while later — so the network diagram rendered from SSR data lacks the
  // parsed shareholders. Poll the endpoint until the backend reports the parse
  // finished, then swap in the fresh gesellschafter data and bump the reload
  // token so CompanyNetworkView refetches its graph.
  const [networkReloadToken, setNetworkReloadToken] = useState(0);
  useEffect(() => {
    if (!gesellschafterData) return;
    if (!gesellschafterData.gesellschafter_backfill_triggered) {
      debugLog(
        '[gesellschafter-poll] no parse in flight (backfill_triggered=false) — not polling',
        { euId, has_liste: gesellschafterData.has_gesellschafterliste },
      );
      return;
    }

    const POLL_MS = 5_000;
    const MAX_ATTEMPTS = 24; // give the parse ~2 minutes, then stop quietly
    let attempts = 0;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    debugLog(
      `[gesellschafter-poll] parse triggered — polling every ${POLL_MS / 1000}s (max ${MAX_ATTEMPTS})`,
      { euId },
    );

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await fetch(
          `/api/companies/${euId}/unified/gesellschafter`,
        );
        if (!cancelled && res.ok) {
          const json = await res.json();
          const data = json.data as CompanyGesellschafterResponse | undefined;
          debugLog(
            `[gesellschafter-poll] attempt ${attempts}/${MAX_ATTEMPTS}`,
            {
              backfill_triggered: data?.gesellschafter_backfill_triggered,
              has_liste: data?.has_gesellschafterliste,
              gesellschafter: data?.gesellschafter.length,
            },
          );
          // Parse done: the backend no longer reports an in-flight backfill
          // and has parsed shareholders to show.
          if (
            data &&
            !data.gesellschafter_backfill_triggered &&
            (data.gesellschafter.length > 0 || data.has_gesellschafterliste)
          ) {
            debugLog(
              '[gesellschafter-poll] parse complete — reloading network diagram',
            );
            setGesellschafterData(data);
            setNetworkReloadToken((t) => t + 1);
            return;
          }
        }
      } catch {
        // transient network error — keep polling until the attempt cap
      }
      if (!cancelled && attempts < MAX_ATTEMPTS) {
        timer = setTimeout(poll, POLL_MS);
      } else if (!cancelled) {
        debugLog(
          '[gesellschafter-poll] gave up after max attempts — parse still not done',
        );
      }
    };

    timer = setTimeout(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [euId, gesellschafterData]);

  // Guest first-visit AI summary trigger. Guests skip fetchData() so we
  // can only gate on the SSR basicData (no financialsData available).
  // basicData.metrics is the closest proxy for "has financial data worth
  // summarising". The server action checks SystemLog for prior visits
  // by the same guestSub fingerprint and only fires once per IP+UA.
  useEffect(() => {
    if (sessionStatus === 'loading') return;
    if (session) return;
    if (!basicData) return;
    if (aiSummaryFiredRef.current === euId) return;
    if (basicData.ai_summary || basicData.ai_summary_en) return;

    const hasFinancialSignal = (basicData.metrics?.length ?? 0) > 0;
    if (!hasFinancialSignal) return;

    aiSummaryFiredRef.current = euId;

    const controller = new AbortController();
    void triggerAiSummaryForGuestIfFirstVisit(euId)
      .then((res) => {
        if (controller.signal.aborted) return;
        if (res.success && res.basicData) {
          handleSetBasicData(res.basicData);
        }
      })
      .catch(() => {});

    return () => controller.abort();
  }, [euId, session, sessionStatus, basicData, handleSetBasicData]);

  // Track company views in GA4.
  useEffect(() => {
    if (basicData?.eu_id) {
      trackCompanyView(
        basicData.eu_id,
        basicData.display_name || basicData.eu_id,
      );
    }
  }, [session, basicData?.eu_id, basicData?.display_name]);

  // Record each company once per mount, even when the session refetches.
  useEffect(() => {
    if (
      session?.user?.sub &&
      basicData?.eu_id &&
      !recordedCompanyViewsRef.current.has(basicData.eu_id)
    ) {
      recordedCompanyViewsRef.current.add(basicData.eu_id);
      recordCompanyView(buildCompanyViewInput(basicData));
    }
  }, [session?.user?.sub, basicData, recordCompanyView]);

  // ==========================================================================
  // EFFECTIVE DATA - Derive from basicData for guests, use fetched data for
  // authenticated users. This lets guest SSR render all visible sections.
  // ==========================================================================

  // WARNING: basicData.overrides comes from verified company owners and MUST
  // take highest priority over register, AI, and backfill data wherever they
  // overlap (address fields, about_us). Never let another source override these.
  const effectiveRegisterData = useMemo(
    () => getEffectiveCompanyRegisterData(basicData, registerData),
    [registerData, basicData],
  );

  // /gesellschafter answers after its page-load DK visit, so its catalog is
  // fresher when non-empty. Keep documents and completeness from that same source.
  const registerDocumentsSource = gesellschafterData?.register_documents?.length
    ? gesellschafterData
    : effectiveRegisterData;

  const effectiveFinancialsData = useMemo(
    () => getEffectiveCompanyFinancialsData(basicData, financialsData, euId),
    [financialsData, basicData, euId],
  );

  // ==========================================================================
  // COMPUTED DATA - Memoized to prevent unnecessary re-renders
  // ==========================================================================

  const hasGesellschafterliste =
    basicData?.has_gesellschafterliste ||
    gesellschafterData?.has_gesellschafterliste ||
    false;
  const gesellschafterItems = gesellschafterData?.gesellschafter ?? [];

  const heroData = useMemo(
    () => buildCompanyProfileHeroData(
      basicData, effectiveRegisterData, companyHistory, euId,
    ),
    [basicData, effectiveRegisterData, companyHistory, euId],
  );

  const statistics = useMemo(
    () => buildCompanyProfileStatistics(effectiveFinancialsData, t, fmt),
    [effectiveFinancialsData, t, fmt],
  );

  // Convert financial tables for existing components
  const aktivaData = useMemo<BalanceSheetFlatRow[] | null>(() => {
    return buildAktivaRows(effectiveFinancialsData);
  }, [effectiveFinancialsData]);

  const passivaData = useMemo<BalanceSheetFlatRow[] | null>(() => {
    return buildPassivaRows(effectiveFinancialsData);
  }, [effectiveFinancialsData]);

  const guvData = useMemo<GuvFlatRow[] | null>(() => {
    return buildGuvRows(effectiveFinancialsData);
  }, [effectiveFinancialsData]);

  const aiGuvData = useMemo<GuvFlatRow[] | null>(() => {
    return buildAiGuvRows(effectiveFinancialsData);
  }, [effectiveFinancialsData]);

  const mergedGuvData = useMemo(() => {
    return [...(guvData || []), ...(aiGuvData || [])];
  }, [guvData, aiGuvData]);

  // Mirror CompanyCostsPieChart's data condition so the chart/table toggle is
  // only rendered when at least one year has recognizable cost categories —
  // otherwise both the pie and the table return null and we'd show an empty
  // toggle bar.
  const hasCostBreakdown = useMemo(() => {
    return computeHasCostBreakdown(mergedGuvData, t);
  }, [mergedGuvData, t]);

  // Unique years present in financial data (for admin correction form)
  const availableFinancialYears = useMemo(() => {
    return collectAvailableFinancialYears(effectiveFinancialsData);
  }, [effectiveFinancialsData]);

  // Revenue history for trend chart
  const revenueHistory = useMemo(() => {
    return buildRevenueHistory(effectiveFinancialsData);
  }, [effectiveFinancialsData]);

  // Mirrors FinancialDocsCard's own filter — the mobile action bar must not
  // offer a "Bilanz" jump to a card that hides itself for lack of downloadable
  // file types.
  const hasFinancialDocs = useMemo(
    () => hasDownloadableFinancialDocs(effectiveFinancialsData),
    [effectiveFinancialsData],
  );

  // Validation errors for admin display
  const validationErrors = useMemo(() => {
    return buildValidationErrors(effectiveFinancialsData);
  }, [effectiveFinancialsData]);

  // ==========================================================================
  // RENDER
  // ==========================================================================

  if (loading) {
    return <CompanyDetailLoading />;
  }

  if (!basicData && !permissionDenied.has('basic')) {
    return null;
  }

  const country = companyCountryCode(basicData?.register_type, basicData?.eu_id);
  // German Handelsregister documents (card + mobile action) exist for DE only.
  const hasRegisterDocs = COMPANY_COUNTRIES[country].germanRegisterDocuments;
  // Swiss register data is credited by the page-level RegisterSourceNote
  // instead; DE credits the Handelsregister, AT the Firmenbuch (per card).
  const showRegisterAttribution = !!basicData && country !== 'CH';
  const attributionRegisterId = formatRegisterNumber(
    basicData?.register_type,
    basicData?.register_id,
  );

  return (
    <div id="company-detail-print" className="bg-background fd-scale-xl">
      {/* Freeze forensics for the sporadic detail-page lockups: long-task
          observer + hard-freeze watchdog beaconing to /api/log/client-freeze.
          Company + network size in context so frozen sessions are attributable
          to a specific company's data shape. */}
      <FreezeReporter
        context={{
          page: 'company-detail',
          euId,
          networkNodes: ssrNetworkData?.nodes.length ?? 0,
          networkEdges: ssrNetworkData?.edges.length ?? 0,
        }}
      />
      {/* pb-24 below lg keeps the last card clear of the fixed action bar. */}
      <Container className="flex flex-col pt-0 pb-24 lg:pb-8 px-0 lg:px-0">
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] overflow-hidden border-b border-border">
          {/* Left Column - Top (Profile, Managers, Register, Subsidiaries).
              `contents` below lg dissolves this wrapper so its cards become
              direct grid items and MOBILE_ORDER can interleave them with the
              sidebar's; `lg:flex` restores the desktop column. */}
          <CompanyDetailMainColumn
            basicData={basicData}
            effectiveRegisterData={effectiveRegisterData}
            effectiveFinancialsData={effectiveFinancialsData}
            contactData={contactData}
            heroData={heroData}
            statistics={statistics}
            loadingBackground={loadingBackground}
            euId={euId}
            ssrNetworkData={ssrNetworkData}
            topEmployerRank={topEmployerRank}
            networkReloadToken={networkReloadToken}
            insolvencyRecords={insolvencyRecords}
            legalStatus={legalStatus}
            hasGesellschafterliste={hasGesellschafterliste}
            country={country}
            gesellschafterItems={gesellschafterItems}
            companyHistory={companyHistory}
            session={session}
            showRegisterAttribution={showRegisterAttribution}
            attributionRegisterId={attributionRegisterId}
            isAdminUser={isAdminUser}
            aktivaData={aktivaData}
            passivaData={passivaData}
            guvData={guvData}
            aiGuvData={aiGuvData}
            t={t}
            validationErrors={validationErrors}
            availableFinancialYears={availableFinancialYears}
          />

          {/* Right Column - Sidebar (Financials, Register, Kontakt, Costs).
              Same `contents` trick as the left column — see MOBILE_ORDER. */}
          <CompanyDetailSidebarColumn
            revenueHistory={revenueHistory}
            mergedGuvData={mergedGuvData}
            heroData={heroData}
            hasRegisterDocs={hasRegisterDocs}
            effectiveRegisterData={effectiveRegisterData}
            registerDocumentsSource={registerDocumentsSource}
            gesellschafterData={gesellschafterData}
            basicData={basicData}
            hasGesellschafterliste={hasGesellschafterliste}
            euId={euId}
            session={session}
            loadingRegister={loadingRegister}
            loadingGesellschafter={loadingGesellschafter}
            showRegisterAttribution={showRegisterAttribution}
            attributionRegisterId={attributionRegisterId}
            contactData={contactData}
            effectiveFinancialsData={effectiveFinancialsData}
            isProOrAdmin={isProOrAdmin}
            hasCostBreakdown={hasCostBreakdown}
            i18n={i18n}
          />
        </div>
        {/* Register data attribution and modification notice. */}
        {basicData && (
          <RegisterSourceNote
            country={country}
            locationAttribution={basicData.location?.attribution}
          />
        )}
      </Container>
      {/* The register-document action is available for German companies. */}
      <MobileActionBar
        euId={basicData?.eu_id ?? euId}
        isAuthenticated={!!session}
        financialDocsAnchor={FINANCIAL_DOCS_ANCHOR}
        registerDocsAnchor={REGISTER_DOCS_ANCHOR}
        hasFinancialDocs={hasFinancialDocs}
        hasRegisterDocs={hasRegisterDocs}
      />
    </div>
  );
}

export default CompanyDetailPage;
